import React, { useState } from 'react';
import API from '../utils/const';
import { Tooltip } from 'react-tooltip'
import LoadingOverlay from './LoadingOverlay';
import { useTranslation } from 'react-i18next';
import { Flex, Button, Accordion, Group, Text, Box, Popover, ActionIcon, Stack } from '@mantine/core';
import { IconReload, IconTrash } from '@tabler/icons-react';
import { handleApiError } from '../utils/app';
import { createToast } from './Toast';
import { CalendarProvider, MessageKind } from '../utils/common';
import { ICSLogoSVG, GoogleSVG, OutlookSVG } from './SVG';

export interface Calendar {
    uuid: string;
    name: string;
    readonly: boolean;
    primary: boolean;
    platform_id: string;
}

export interface Account {
    uuid: string;
    key: string;
    provider: CalendarProvider;
    calendars: Calendar[] | null;
}


const AccountCard: React.FC<{ account: Account }> = ({ account }) => {
    const { t } = useTranslation(['app']);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const refreshCalendars = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(
                `${API}/accounts/${account.uuid}/calendars/refresh`,
                {
                    credentials: 'include',
                    method: 'POST'
                }
            )
            if (!response.ok) {
                handleApiError(response, t, true)
            }
            else {
                window.location.reload()
            }
        } finally {
            setIsLoading(false);
        }
    }

    const refreshCalendarEvents = async (calendar_uuid: string) => {
        setIsLoading(true);
        try {
            const response = await fetch(
                `${API}/calendars/${calendar_uuid}/resync`,
                {
                    credentials: 'include',
                    method: 'POST'
                }
            )
            if (response.ok) {
                createToast("All rules that use this calendar are going to be refreshed.", MessageKind.Info);
            } else {
                handleApiError(response, t, true)
            }
        } finally {
            setIsLoading(false);
        }
    }

    const deleteAccount = async (account_uuid: string) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API}/accounts/${account_uuid}`, {
                credentials: 'include',
                method: 'DELETE'
            })
            if (response.ok) {
                window.location.reload()
            } else {
                handleApiError(response, t, true)
            }
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            {isLoading &&
                <LoadingOverlay />
            }
            <Accordion.Item key={account.uuid} value={account.uuid}>
                <Accordion.Control>
                    <Group>
                        <Box w={24} h={24}>
                            {account.provider === CalendarProvider.ICS && <ICSLogoSVG />}
                            {account.provider === CalendarProvider.GOOGLE && <GoogleSVG />}
                            {account.provider === CalendarProvider.OUTLOOK && <OutlookSVG />}
                        </Box>
                        <Text fw={500} fz='md'>{account.key}</Text>
                    </Group>
                </Accordion.Control>
                <Accordion.Panel>
                    <Flex direction='column' gap='md' px='md'>
                        <Group justify='flex-end'>
                            <Popover width={200} position="bottom" withArrow shadow="md">
                                <Popover.Target>
                                    <ActionIcon variant='outline' color='red' onClick={() => deleteAccount(account.uuid)}>{<IconTrash stroke={1} />}</ActionIcon>
                                </Popover.Target>
                                <Popover.Dropdown>
                                    <Stack gap='sm'>
                                        <Text size="sm">
                                            Warning: Make sure all synchronization rules are deleted, and all synchronized events have been deleted. It's advised to wait at least 15 minutes before deleting an account.
                                        </Text>
                                        <Button variant='outline' color='red' onClick={() => deleteAccount(account.uuid)}>Confirm</Button>
                                    </Stack>
                                </Popover.Dropdown>
                            </Popover>
                        </Group>
                        {account.provider !== CalendarProvider.ICS &&
                            <Group justify='flex-end'>
                                <Button onClick={refreshCalendars}>{t("dashboard.refresh-calendars")}</Button>
                            </Group>
                        }
                        {account.calendars && account.calendars.map((calendar) => (
                            <Group justify='space-between' key={calendar.uuid}>
                                <Text fw={500} fz='sm'>{calendar.name}</Text>
                                <Button variant='outline' leftSection={<IconReload stroke={1} />} onClick={() => refreshCalendarEvents(calendar.uuid)}>
                                    {t("dashboard.refresh-events")}
                                </Button>
                            </Group>
                        ))}
                    </Flex>
                </Accordion.Panel>
            </Accordion.Item>
            <Tooltip style={{ zIndex: 999 }} anchorSelect='.google-calendar-tooltip'>This is a Google generated calendar. <br></br>It is read-only, so events can be copied from it, but not to it.</Tooltip>
        </>
    );
};

export default AccountCard;
